import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { MoonLoader } from "react-spinners";
import clientConfig from "../../config";
import { getAuthHeaders } from "../../utils/apiFetch";
import styles from "../../pages/AccidentReportTable.module.css";
import DeleteIcon from "../icons/DeleteIcon";
import DownloadIcon from "../icons/DownloadIcon";
import { useUser } from "../../contexts/UserContext";
import Button from "../UiElements/Button";
import Checkbox from "../UiElements/Checkbox";
import Dropdown, { type DropdownItem } from "../UiElements/Dropdown";
import Input from "../UiElements/Input";
import ErrorMessage from "../UiElements/ErrorMessage";
import { tokens } from "../../design-system/tokens";
import { useDraggingScroll } from "../../hooks/useDraggingScroll";

const SearchInput = memo(
  ({
    searchQuery,
    setSearchQuery,
    searchActive,
    handleClearSearch,
    searchInputRef,
  }: {
    searchQuery: string;
    setSearchQuery: (v: string) => void;
    searchActive: boolean;
    handleClearSearch: () => void;
    searchInputRef: React.RefObject<HTMLInputElement | null>;
  }) => (
    <div className={styles.searchContainer}>
      <Input
        ref={searchInputRef}
        type="text"
        placeholder="חיפוש לפי קובץ, משתמש, אימייל, נושא, סטטוס..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        color="outlineSecondary"
        size="medium"
        rtl
        width="100%"
        maxWidth={500}
      />
      {searchActive ? (
        <Button
          label="נקה"
          color="outlineSecondary"
          size="small"
          width="auto"
          onClick={handleClearSearch}
        />
      ) : null}
    </div>
  )
);

export interface FormSubmissionRow {
  _id: string;
  ownerId: string;
  emailTo: string;
  emailTitle: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  emailStatus: string;
  emailError: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt?: string;
}

const FILE_PROXY = `${clientConfig.backendUrl}/forms/accident-report/proxy-file`;

function formatFileSize(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} ק״ב`;
  return `${(n / (1024 * 1024)).toFixed(1)} מ״ב`;
}

function translateEmailStatus(status: string): string {
  const map: Record<string, string> = {
    pending: "בהמתנה",
    sent: "נשלח",
    failed: "נכשל",
  };
  return map[status] || status || "-";
}

export type SubmissionApiSegment =
  | "big-car-submissions"
  | "maagar-submissions"
  | "electric-cars-submissions"
  | "driver-statement-submissions"
  | "new-driver-statement-submissions"
  | "family-form-submissions";

interface SubmissionFormsTableProps {
  apiSegment: SubmissionApiSegment;
  title: string;
  emptyLabel: string;
  formPageUrl?: string;
}

const SubmissionFormsTable: React.FC<SubmissionFormsTableProps> = ({
  apiSegment,
  title,
  emptyLabel,
  formPageUrl,
}) => {
  const { isAuthenticated } = useUser();
  const [rows, setRows] = useState<FormSubmissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tableScrollProps = useDraggingScroll();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(
    null
  );
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    ids: string[];
    label?: string;
    isBulk: boolean;
  }>({ show: false, ids: [], label: "", isBulk: false });

  const loadRows = useCallback(
    async (pageNum: number = 1, append: boolean = false) => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = `${clientConfig.backendUrl}/forms/${apiSegment}`;
        const params = new URLSearchParams({
          page: String(pageNum),
          limit: "100",
        });
        const response = await fetch(`${apiUrl}?${params}`, { headers: getAuthHeaders() });

        if (!response.ok) {
          const contentType = response.headers.get("content-type");
          let errorMessage = `HTTP error! status: ${response.status}`;
          if (contentType && contentType.includes("application/json")) {
            try {
              const errorData = await response.json();
              errorMessage = errorData.error || errorMessage;
            } catch {
              //
            }
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        const newItems: FormSubmissionRow[] = data.items || [];
        const totalPages: number = data.totalPages ?? 1;

        if (append) {
          setRows((prev) => [...prev, ...newItems]);
        } else {
          setRows(newItems);
        }
        setHasMore(pageNum < totalPages);
        setPage(pageNum);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "שגיאה בטעינת הנתונים";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [apiSegment]
  );

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadRows(page + 1, true);
    }
  }, [loading, hasMore, page, loadRows]);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadRows(1, false);
  }, [loadRows]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => rows.some((r) => r._id === id))
    );
  }, [rows]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !searchActive) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );
    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
      observer.disconnect();
    };
  }, [hasMore, loading, handleLoadMore, searchActive]);

  useEffect(() => {
    if (searchQuery === "") {
      setSearchActive(false);
      return;
    }
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      const q = searchQuery.trim();
      setSearchActive(!!q);
    }, 300);
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [searchQuery]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter((r) => {
      return (
        (r.fileName || "").toLowerCase().includes(query) ||
        (r.ownerId || "").toLowerCase().includes(query) ||
        (r.emailTo || "").toLowerCase().includes(query) ||
        (r.emailTitle || "").toLowerCase().includes(query) ||
        (r.emailStatus || "").toLowerCase().includes(query) ||
        (r.emailError || "").toLowerCase().includes(query) ||
        formatDate(r.createdAt).toLowerCase().includes(query)
      );
    });
  }, [rows, searchQuery]);

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const pathParam =
        fileUrl.startsWith("http://") || fileUrl.startsWith("https://")
          ? fileUrl
          : fileUrl.replace(/^\/+/, "");
      const url = `${FILE_PROXY}?path=${encodeURIComponent(pathParam)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Error downloading file:", err);
      alert("שגיאה בהורדת הקובץ. נסה שוב.");
    }
  };

  const deleteByIds = useCallback(
    async (ids: string[]) => {
      try {
        setBulkDeleting(true);
        await Promise.all(
          ids.map(async (id) => {
            const response = await fetch(
              `${clientConfig.backendUrl}/forms/${apiSegment}/${id}`,
              { method: "DELETE", headers: getAuthHeaders() }
            );
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
          })
        );
        setRows((prev) => prev.filter((item) => !ids.includes(item._id)));
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        setDeleteConfirm({ show: false, ids: [], label: "", isBulk: false });
      } catch {
        alert("שגיאה במחיקה. נסה שוב.");
      } finally {
        setBulkDeleting(false);
      }
    },
    [apiSegment]
  );

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filteredRows.map((r) => r._id));
    } else {
      setSelectedIds([]);
    }
    setLastSelectedIndex(null);
  };

  const toggleSelectRow = (
    row: FormSubmissionRow,
    indexInFiltered: number,
    shiftKey: boolean
  ) => {
    setSelectedIds((prev) => {
      let updated = [...prev];
      const isSelected = updated.includes(row._id);
      if (shiftKey && lastSelectedIndex !== null) {
        const [start, end] = [
          Math.min(lastSelectedIndex, indexInFiltered),
          Math.max(lastSelectedIndex, indexInFiltered),
        ];
        const rangeIds = filteredRows
          .slice(start, end + 1)
          .map((item) => item._id);
        if (isSelected) {
          updated = updated.filter((id) => !rangeIds.includes(id));
        } else {
          rangeIds.forEach((id) => {
            if (!updated.includes(id)) updated.push(id);
          });
        }
      } else if (isSelected) {
        updated = updated.filter((id) => id !== row._id);
      } else {
        updated.push(row._id);
      }
      return updated;
    });
    setLastSelectedIndex(indexInFiltered);
  };

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchActive(false);
  }, []);

  const colCount = 8 + (isAuthenticated ? 2 : 0);

  const DeleteConfirmDialog = deleteConfirm.show ? (
    <div className={styles.ConfirmDelete}>
      <div className={styles.TitleDelete} style={tokens.typography.h6}>
        {deleteConfirm.isBulk
          ? `למחוק ${deleteConfirm.ids.length} רשומות שנבחרו?`
          : `למחוק את הרשומה "${deleteConfirm.label}"?`}
      </div>
      <div className={styles.BtContainer}>
        <Button
          label={bulkDeleting ? "מוחק..." : "מחק"}
          color="primary"
          size="medium"
          width={120}
          onClick={() => deleteByIds(deleteConfirm.ids)}
          disabled={bulkDeleting}
        />
        <Button
          label="ביטול"
          color="outlineSecondary"
          size="medium"
          width={120}
          onClick={() =>
            setDeleteConfirm({ show: false, ids: [], label: "", isBulk: false })
          }
        />
      </div>
    </div>
  ) : null;

  const EmptyState =
    !loading && rows.length === 0 ? (
      <div className={styles.EmptyState}>{emptyLabel}</div>
    ) : searchActive && filteredRows.length === 0 ? (
      <div className={styles.EmptyState}>לא נמצאו תוצאות לחיפוש</div>
    ) : null;

  const bulkDropdownItems: DropdownItem[] = [
    {
      id: "delete",
      label: bulkDeleting ? "מוחק..." : "מחק נבחרים",
      onClick: () => {
        if (!selectedIds.length) return;
        setDeleteConfirm({
          show: true,
          ids: selectedIds,
          isBulk: true,
        });
      },
      disabled: !selectedIds.length || bulkDeleting || loading,
    },
  ];

  return (
    <div className={styles.Container} dir="rtl">
      <div className={styles.HeaderContainer}>
        <div className={styles.Title} style={tokens.typography.h2}>
          {title}
        </div>
        <SearchInput
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchActive={searchActive}
          handleClearSearch={handleClearSearch}
          searchInputRef={searchInputRef}
        />
      </div>
      {DeleteConfirmDialog}
      {loading && rows.length === 0 ? (
        <div className={styles.LoadingContainer}>
          <MoonLoader size={50} color={tokens.colors.secondary} />
        </div>
      ) : error ? (
        <div className={styles.ErrorState}>
          <ErrorMessage message={error} size="medium" />
          <Button
            label="נסה שוב"
            color="secondary"
            size="medium"
            width={120}
            onClick={() => loadRows(1, false)}
          />
        </div>
      ) : (
        <>
          {EmptyState}
          {(rows.length > 0 || searchActive) && (
            <div className={styles.TableWrapper}>
              <div className={styles.TableInner}>
              <div className={styles.BulkActions}>
                {isAuthenticated && (
                  <div className={styles.BulkActionsGroup}>
                    <Dropdown
                      triggerLabel="פעולות"
                      triggerColor="outlineSecondary"
                      triggerSize="medium"
                      items={bulkDropdownItems}
                      ariaLabel="פעולות על נבחרים"
                    />
                    {selectedIds.length ? (
                      <span className={styles.SelectedCount}>
                        {`${selectedIds.length} נבחרו`}
                      </span>
                    ) : null}
                  </div>
                )}
                {formPageUrl ? (
                  <div className={styles.LinkContainer}>
                    <a
                      href={formPageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...tokens.typography.body,
                        color: tokens.colors.text.secondary,
                        textDecoration: "underline",
                      }}
                    >
                      לעמוד הטופס
                    </a>
                  </div>
                ) : (
                  <span />
                )}
              </div>
              <div className={styles.TableFrame}>
              <div
                ref={tableScrollProps.ref}
                className={`${styles.TableScroll} ${tableScrollProps.isDraggingScroll ? styles.TableScrollDragging : ""}`}
                onMouseDown={tableScrollProps.onMouseDown}
                style={tableScrollProps.style}
              >
              <Table className={styles.Table}>
                <Thead>
                  <Tr className={styles.TrHeader}>
                    {isAuthenticated && (
                      <Th
                        className={`${styles.ThTitle} ${styles.SelectHeader}`}
                      >
                        <Checkbox
                          name="select-all-submissions"
                          checked={
                            filteredRows.length > 0 &&
                            selectedIds.length === filteredRows.length &&
                            filteredRows.every((r) =>
                              selectedIds.includes(r._id)
                            )
                          }
                          semiSelected={
                            selectedIds.length > 0 &&
                            selectedIds.length < filteredRows.length
                          }
                          onChange={(e) =>
                            toggleSelectAll(e.target.checked)
                          }
                          size="small"
                          color="onPrimary"
                          className={`${styles.TableCheckbox} ${styles.hideOnMobile}`}
                          ariaLabel="בחר הכל"
                        />
                      </Th>
                    )}
                    <Th className={styles.ThTitle}>שם קובץ</Th>
                    <Th className={styles.ThTitle}>מזהה משתמש</Th>
                    <Th className={styles.ThTitle}>אימייל נמען</Th>
                    <Th className={styles.ThTitle}>כותרת מייל</Th>
                    <Th className={styles.ThTitle}>סטטוס מייל</Th>
                    <Th className={styles.ThTitle}>גודל קובץ</Th>
                    <Th className={styles.ThTitle}>תאריך יצירה</Th>
                    <Th className={styles.ThTitle}>הורדה</Th>
                    {isAuthenticated && (
                      <Th className={styles.ThTitle}>מחק</Th>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredRows.map((row, index) => {
                    return (
                      <Tr key={row._id} className={styles.TrData}>
                        {isAuthenticated && (
                          <Td
                            className={`${styles.SelectCell} ${styles.SelectHeader}`}
                          >
                            <Checkbox
                              name={`select-row-${row._id}`}
                              checked={selectedIds.includes(row._id)}
                              onChange={(e) =>
                                toggleSelectRow(
                                  row,
                                  index,
                                  (e.nativeEvent as MouseEvent).shiftKey
                                )
                              }
                              size="small"
                              className={styles.TableCheckbox}
                              ariaLabel={`בחר ${row.fileName || "רשומה"}`}
                            />
                          </Td>
                        )}
                        <Td
                          className={styles.FileNameContainer}
                          data-label="שם קובץ"
                        >
                          {row.fileName || "-"}
                        </Td>
                        <Td
                          className={styles.DriverIdContainer}
                          data-label="מזהה משתמש"
                        >
                          {row.ownerId || "-"}
                        </Td>
                        <Td
                          className={styles.EmailContainer}
                          data-label="אימייל נמען"
                        >
                          {row.emailTo || "-"}
                        </Td>
                        <Td
                          className={styles.CompanyContainer}
                          data-label="כותרת מייל"
                        >
                          {row.emailTitle || "-"}
                        </Td>
                        <Td data-label="סטטוס מייל">
                          <div>{translateEmailStatus(row.emailStatus)}</div>
                          {row.emailStatus === "failed" && row.emailError ? (
                            <div
                              className={styles.NotesContent}
                              style={{
                                fontSize: 12,
                                color: tokens.colors.error,
                              }}
                            >
                              {row.emailError}
                            </div>
                          ) : null}
                        </Td>
                        <Td data-label="גודל קובץ">
                          {formatFileSize(row.fileSize)}
                        </Td>
                        <Td
                          className={styles.DateContainer}
                          data-label="תאריך יצירה"
                        >
                          {formatDate(row.createdAt)}
                        </Td>
                        <Td className={styles.DownloadContainer} data-label="הורדה">
                          <div
                            onClick={() =>
                              handleDownload(row.fileUrl, row.fileName)
                            }
                            className={styles.DownloadButton}
                            title="הורד קובץ"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleDownload(row.fileUrl, row.fileName);
                              }
                            }}
                          >
                            <DownloadIcon />
                          </div>
                        </Td>
                        {isAuthenticated && (
                          <Td className={styles.RemoveContainer} data-label="מחק">
                            <div
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  setDeleteConfirm({
                                    show: true,
                                    ids: [row._id],
                                    label: row.fileName,
                                    isBulk: false,
                                  });
                                }
                              }}
                              onClick={() =>
                                setDeleteConfirm({
                                  show: true,
                                  ids: [row._id],
                                  label: row.fileName,
                                  isBulk: false,
                                })
                              }
                              className={styles.RowDeleteButton}
                            >
                              <div className={styles.Delicon}>
                                <DeleteIcon />
                              </div>
                            </div>
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                  {loading && hasMore && (
                    <Tr>
                      <Td colSpan={colCount} className={styles.LoadingMore}>
                        טוען עוד...
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
              </div>
              </div>
              {rows.length > 10 && hasMore && !searchActive && (
                <div ref={loadMoreRef} className={styles.LoadMoreTrigger} />
              )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubmissionFormsTable;
